import React, { useState, useEffect, useMemo } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import Tabs from '../common/Tabs';
import DateRangePicker from '../common/DateRangePicker';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import '../../styles/reports.css';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount || 0);
};

const Reports = () => {
  const { hasModule } = useAuth();
  const [activeTab, setActiveTab] = useState('sales');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(1)),
    endDate: new Date()
  });

  const tabs = useMemo(() => {
    const all = [
      { id: 'sales', label: 'Sales Report', module: 'pos' },
      { id: 'inventory', label: 'Inventory Report', module: 'inventory' },
      { id: 'financial', label: 'Financial Report', module: 'finance' },
      { id: 'customers', label: 'Customer Report', module: 'crm' },
      { id: 'debts', label: 'Debt Report', module: 'finance' }
    ];
    return all.filter((t) => hasModule(t.module));
  }, [hasModule]);

  useEffect(() => {
    if (tabs.length && !tabs.find((t) => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const loadReport = async () => {
    if (!dateRange.startDate || !dateRange.endDate || !tabs.length) return;
    setLoading(true);
    try {
      const start = dateRange.startDate.toISOString().split('T')[0];
      const end = dateRange.endDate.toISOString().split('T')[0];
      const response = await api.get(`/reports.php/${activeTab}?from_date=${start}&to_date=${end}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to load report:', error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [activeTab, dateRange]);

  const renderReportContent = () => {
    if (loading) return <div className="loading-spinner">Loading report data...</div>;
    if (!reportData) return <div className="no-data">No data available for this period.</div>;

    switch(activeTab) {
      case 'sales':
        return <SalesReport data={reportData} />;
      case 'inventory':
        return <InventoryReport data={reportData} />;
      case 'financial':
        return <FinancialReport data={reportData} />;
      case 'customers':
        return <CustomerReport data={reportData} />;
      case 'debts':
        return <DebtReport data={reportData} />;
      default:
        return null;
    }
  };

  if (!tabs.length) {
    return (
      <div className="reports">
        <h1>Reports & Analytics</h1>
        <p>Enable apps under Settings → Subscription to unlock module reports.</p>
      </div>
    );
  }

  return (
    <div className="reports">
      <div className="reports-header">
        <h1>Reports & Analytics</h1>
        <div className="header-actions">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={setDateRange}
          />
          <Button variant="primary" icon="📥">
            Export Report
          </Button>
        </div>
      </div>

      <Card>
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="report-content">
          {renderReportContent()}
        </div>
      </Card>
    </div>
  );
};

// Sub-components for different reports
const SalesReport = ({ data }) => {
  const { summary, daily, payment_methods } = data;
  return (
    <div className="report-section">
      <h3>Sales Summary</h3>
      <div className="summary-cards">
        <div className="summary-card">
          <span className="label">Total Sales</span>
          <span className="value">{formatCurrency(summary?.total_revenue)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Transactions</span>
          <span className="value">{summary?.total_transactions || 0}</span>
        </div>
        <div className="summary-card">
          <span className="label">Average Sale</span>
          <span className="value">{formatCurrency(summary?.average_transaction)}</span>
        </div>
      </div>
      
      <div className="daily-sales-table">
        <h4>Detailed Sales Breakdown</h4>
        <table className="data-table">
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>Invoice Number</th>
              <th>Product</th>
              <th>Quantity</th>
              <th>Total Amount</th>
              <th>Payment Method</th>
            </tr>
          </thead>
          <tbody>
            {(data.detailed_sales || []).map((row, index) => (
              <tr key={index}>
                <td>{new Date(row.created_at).toLocaleString()}</td>
                <td>{row.invoice_number}</td>
                <td>{row.product_name}</td>
                <td>{row.quantity}</td>
                <td>{formatCurrency(row.total_amount)}</td>
                <td style={{ textTransform: 'capitalize' }}>{row.payment_method}</td>
              </tr>
            ))}
            {(!data.detailed_sales || data.detailed_sales.length === 0) && (
              <tr><td colSpan="6" className="text-center">No detailed sales data for this period</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const InventoryReport = ({ data }) => {
  const { summary, category_summary, low_stock } = data;
  return (
    <div className="report-section">
      <h3>Inventory Summary</h3>
      <div className="summary-cards">
        <div className="summary-card">
          <span className="label">Total Products</span>
          <span className="value">{summary?.total_products || 0}</span>
        </div>
        <div className="summary-card">
          <span className="label">Low Stock Items</span>
          <span className="value">{summary?.low_stock_count || 0}</span>
        </div>
        <div className="summary-card">
          <span className="label">Inventory Value</span>
          <span className="value">{formatCurrency(summary?.total_retail_value)}</span>
        </div>
      </div>
    </div>
  );
};

const FinancialReport = ({ data }) => {
  const { revenue } = data;
  return (
    <div className="report-section">
      <h3>Financial Summary</h3>
      <div className="summary-cards">
        <div className="summary-card">
          <span className="label">Revenue</span>
          <span className="value">{formatCurrency(revenue?.total)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Cost of Goods Sold</span>
          <span className="value">{formatCurrency(revenue?.cost_of_goods)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Gross Profit</span>
          <span className="value">{formatCurrency(revenue?.gross_profit)}</span>
        </div>
      </div>
    </div>
  );
};

const CustomerReport = ({ data }) => {
  const { customers } = data;
  const totalCustomers = customers?.length || 0;
  const activeCustomers = customers?.filter(c => c.transaction_count > 0).length || 0;
  const totalSpend = customers?.reduce((sum, c) => sum + parseFloat(c.total_purchases), 0) || 0;
  const avgPurchaseValue = totalCustomers ? totalSpend / totalCustomers : 0;

  return (
    <div className="report-section">
      <h3>Customer Summary</h3>
      <div className="summary-cards">
        <div className="summary-card">
          <span className="label">Total Customers</span>
          <span className="value">{totalCustomers}</span>
        </div>
        <div className="summary-card">
          <span className="label">Active (Purchased)</span>
          <span className="value">{activeCustomers}</span>
        </div>
        <div className="summary-card">
          <span className="label">Avg. Customer Value</span>
          <span className="value">{formatCurrency(avgPurchaseValue)}</span>
        </div>
      </div>
    </div>
  );
};

const DebtReport = ({ data }) => {
  const { summary } = data;
  
  // Calculate totals from the summary array {status, count, total_original, total_remaining}
  const totalOutstanding = summary?.reduce((sum, s) => sum + parseFloat(s.total_remaining), 0) || 0;
  const overdueDebt = summary?.find(s => s.status === 'overdue')?.total_remaining || 0;
  const totalDebtors = summary?.reduce((sum, s) => sum + parseInt(s.count), 0) || 0;

  return (
    <div className="report-section">
      <h3>Debt Summary</h3>
      <div className="summary-cards">
        <div className="summary-card">
          <span className="label">Total Outstanding</span>
          <span className="value">{formatCurrency(totalOutstanding)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Overdue Debts</span>
          <span className="value">{formatCurrency(overdueDebt)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Total Debtors (Active/Overdue)</span>
          <span className="value">{totalDebtors}</span>
        </div>
      </div>
    </div>
  );
};

export default Reports;